/**
 * Simple flat-scene operations for the inline editor (v2).
 */

import type { Chapter, Mission, Scenario, Scene } from '../types';
import { uniqueId } from '../components/scenario-editor/utils';

export interface InlineChapterGroup {
  chapter: Chapter | null;
  scenes: Scene[];
}

export interface InlineMissionGroup {
  mission: Mission | null;
  chapters: InlineChapterGroup[];
}

export function buildChapterGroups(scenario: Scenario): InlineChapterGroup[] {
  const chapters = scenario.metadata.chapters ?? [];
  const scenes = scenario.scenes;

  if (chapters.length === 0) {
    return [{ chapter: null, scenes: [...scenes] }];
  }

  const sceneIndexById = new Map(scenes.map((s, i) => [s.id, i]));
  const sortedChapters = [...chapters].sort((a, b) => {
    const ia = sceneIndexById.get(a.startScene) ?? Number.MAX_SAFE_INTEGER;
    const ib = sceneIndexById.get(b.startScene) ?? Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });

  const groups: InlineChapterGroup[] = [];
  const firstStartIdx = sceneIndexById.get(sortedChapters[0]!.startScene) ?? 0;

  if (firstStartIdx > 0) {
    groups.push({ chapter: null, scenes: scenes.slice(0, firstStartIdx) });
  }

  for (let i = 0; i < sortedChapters.length; i++) {
    const startIdx = sceneIndexById.get(sortedChapters[i]!.startScene) ?? 0;
    const endIdx =
      i + 1 < sortedChapters.length
        ? (sceneIndexById.get(sortedChapters[i + 1]!.startScene) ?? scenes.length)
        : scenes.length;
    groups.push({ chapter: sortedChapters[i]!, scenes: scenes.slice(startIdx, endIdx) });
  }

  return groups;
}

export function buildInlineStructure(scenario: Scenario): InlineMissionGroup[] {
  const chapterGroups = buildChapterGroups(scenario);
  const missions = [...(scenario.metadata.missions ?? [])].sort((a, b) => a.order - b.order);

  if (missions.length === 0) {
    return [{ mission: null, chapters: chapterGroups }];
  }

  const missionMap = new Map<string | '__none__', InlineChapterGroup[]>();
  for (const mission of missions) {
    missionMap.set(mission.id, []);
  }
  missionMap.set('__none__', []);

  for (const group of chapterGroups) {
    const missionId = group.chapter?.missionId;
    const key = missionId && missionMap.has(missionId) ? missionId : '__none__';
    missionMap.get(key)!.push(group);
  }

  const result: InlineMissionGroup[] = missions.map((mission) => ({
    mission,
    chapters: missionMap.get(mission.id) ?? [],
  }));

  const unassigned = (missionMap.get('__none__') ?? []).filter((g) => g.scenes.length > 0);
  if (unassigned.length > 0) {
    result.push({ mission: null, chapters: unassigned });
  }

  return result;
}

export function findSceneIndex(scenario: Scenario, sceneId: string): number {
  return scenario.scenes.findIndex((s) => s.id === sceneId);
}

export function insertSceneAfter(
  scenario: Scenario,
  afterSceneId: string | null,
  scene: Scene
): Scenario {
  const scenes = [...scenario.scenes];
  if (afterSceneId === null) {
    scenes.unshift(scene);
  } else {
    const idx = findSceneIndex(scenario, afterSceneId);
    scenes.splice(idx >= 0 ? idx + 1 : scenes.length, 0, scene);
  }
  return { ...scenario, scenes };
}

export function removeScene(scenario: Scenario, sceneId: string): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.filter((s) => s.id !== sceneId),
  };
}

export function updateScene(scenario: Scenario, sceneId: string, nextScene: Scene): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => (s.id === sceneId ? nextScene : s)),
  };
}

export function moveScene(scenario: Scenario, sceneId: string, direction: -1 | 1): Scenario {
  const scenes = [...scenario.scenes];
  const idx = scenes.findIndex((s) => s.id === sceneId);
  if (idx < 0) return scenario;
  const target = idx + direction;
  if (target < 0 || target >= scenes.length) return scenario;
  const [item] = scenes.splice(idx, 1);
  scenes.splice(target, 0, item!);
  return { ...scenario, scenes };
}

export function getChapterBounds(
  scenario: Scenario,
  chapterScenes: Scene[]
): { startIdx: number; endIdx: number } | null {
  if (chapterScenes.length === 0) return null;
  const startIdx = findSceneIndex(scenario, chapterScenes[0]!.id);
  const endIdx = findSceneIndex(scenario, chapterScenes[chapterScenes.length - 1]!.id);
  if (startIdx < 0 || endIdx < 0) return null;
  return { startIdx, endIdx };
}

export function updateChapterStartScene(
  scenario: Scenario,
  chapterId: string,
  sceneId: string,
  previousStartScene?: string
): Scenario {
  const chapter = scenario.metadata.chapters?.find((c) => c.id === chapterId);
  if (!chapter) return scenario;
  const oldStartScene = previousStartScene ?? chapter.startScene;

  let next: Scenario = {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      chapters: (scenario.metadata.chapters ?? []).map((c) =>
        c.id === chapterId ? { ...c, startScene: sceneId } : c
      ),
    },
  };

  if (next.metadata.startScene === oldStartScene) {
    next = {
      ...next,
      metadata: { ...next.metadata, startScene: sceneId },
    };
  }

  return next;
}

export function moveSceneInChapter(
  scenario: Scenario,
  chapterId: string | undefined,
  chapterScenes: Scene[],
  sceneId: string,
  direction: -1 | 1
): Scenario {
  const bounds = getChapterBounds(scenario, chapterScenes);
  if (!bounds) {
    return moveScene(scenario, sceneId, direction);
  }

  const scenes = [...scenario.scenes];
  const idx = scenes.findIndex((s) => s.id === sceneId);
  if (idx < 0) return scenario;

  const target = idx + direction;
  if (target < bounds.startIdx || target > bounds.endIdx) return scenario;

  const oldFirstSceneId = chapterScenes[0]!.id;
  const [item] = scenes.splice(idx, 1);
  scenes.splice(target, 0, item!);

  let next: Scenario = { ...scenario, scenes };

  if (chapterId) {
    const newFirstSceneId = scenes[bounds.startIdx]!.id;
    if (newFirstSceneId !== oldFirstSceneId) {
      next = updateChapterStartScene(next, chapterId, newFirstSceneId, oldFirstSceneId);
    }
  }

  return next;
}

export function insertSceneInChapter(
  scenario: Scenario,
  chapterId: string | undefined,
  chapterScenes: Scene[],
  afterSceneId: string | null,
  scene: Scene
): Scenario {
  let insertIdx: number;

  if (afterSceneId === null) {
    if (chapterScenes.length === 0) {
      insertIdx = scenario.scenes.length;
    } else {
      insertIdx = findSceneIndex(scenario, chapterScenes[0]!.id);
      if (insertIdx < 0) insertIdx = scenario.scenes.length;
    }
  } else {
    const idx = findSceneIndex(scenario, afterSceneId);
    insertIdx = idx >= 0 ? idx + 1 : scenario.scenes.length;
  }

  const scenes = [...scenario.scenes];
  scenes.splice(insertIdx, 0, scene);

  let next: Scenario = { ...scenario, scenes };

  if (chapterId && afterSceneId === null) {
    const oldStartScene = chapterScenes[0]?.id;
    next = updateChapterStartScene(next, chapterId, scene.id, oldStartScene);
  }

  return next;
}

export function removeSceneInChapter(
  scenario: Scenario,
  chapterId: string | undefined,
  chapterScenes: Scene[],
  sceneId: string
): Scenario {
  const firstSceneId = chapterScenes[0]?.id;
  const isRemovingStart = sceneId === firstSceneId;
  const nextSceneId = isRemovingStart && chapterScenes.length > 1
    ? chapterScenes[1]!.id
    : undefined;

  let next = removeScene(scenario, sceneId);

  if (chapterId && isRemovingStart && nextSceneId) {
    next = updateChapterStartScene(next, chapterId, nextSceneId, firstSceneId);
  }

  return next;
}

export function createChapterStartScene(sceneId?: string): Scene {
  return {
    id: sceneId ?? uniqueId('scene'),
    type: 'dialogue',
    text: '',
    isInternal: true,
  };
}

export function addInlineMission(scenario: Scenario): Scenario {
  const missions = [...(scenario.metadata.missions ?? [])];
  const mission: Mission = {
    id: uniqueId('mission'),
    title: 'Nouvelle mission',
    order: missions.length + 1,
  };
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      missions: [...missions, mission],
    },
  };
}

export function addInlineChapter(scenario: Scenario, missionId: string): Scenario {
  const sceneId = uniqueId('scene');
  const scene = createChapterStartScene(sceneId);
  const chapter: Chapter = {
    id: uniqueId('chapitre'),
    title: 'Nouveau chapitre',
    startScene: sceneId,
    missionId,
  };
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      chapters: [...(scenario.metadata.chapters ?? []), chapter],
      startScene: scenario.metadata.startScene || sceneId,
    },
    scenes: [...scenario.scenes, scene],
  };
}

export function collectChapterSceneIds(_scenario: Scenario, chapterScenes: Scene[]): Set<string> {
  const ids = new Set<string>();
  for (const scene of chapterScenes) {
    ids.add(scene.id);
    if (scene.type === 'choice') {
      for (const opt of scene.options) {
        for (const nested of opt.scenes ?? []) ids.add(nested.id);
      }
    }
  }
  return ids;
}
